import { useEnvContext } from "context/EnvContext";
import {
  ASSESSMENT_SLUG,
  CARESEEKER_TYPE,
  LIVING_SITUATION_OTHER,
  PAYERS_MAPPING,
  RELATIONSHIP_TO_PATIENT,
  SEARCH_TYPE_CARE,
  SEARCH_TYPE_HOME_CARE,
  SEARCH_TYPE_MEDICAL_SUPPLIES,
  SEARCH_TYPE_REHABILITATION,
} from "core/consts";
import { getPayersLabels } from "core/consts/labels";
import { matchesWhitelistScopes } from "core/model/auctions";
import { getAge, getCareLevelValue, getInterval } from "core/model/patients";
import { getAgeFromDate } from "core/model/utils/dates";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { buildLine, concatStrings } from "core/model/utils/strings";
import {
  Auction,
  CarePreferences as CarePreferencesType,
  Insurance as CostPayerType,
  Financing as FinancingType,
  GeneralPractitioner as GeneralPractitionerType,
  GetOntologyType,
  PatientCommunication as PatientCommunicationType,
  PatientProfile,
  PatientProfile as PatientProfileType,
  Patient as PatientType,
  Payers as PayersType,
  SearchType,
} from "core/types";
import { TEXT_DARK_SECONDARY } from "ds_legacy/materials/colors";
import { HorizontalLayout } from "ds_legacy/materials/layouts";
import { Body } from "ds_legacy/materials/typography";
import {
  carelevelWhitelist,
  grantsWhitelist,
  insuranceWhitelist,
  reimbursment_for_help_at_homeWhitelist,
  reimbursment_securedWhitelist,
  reliefServiceBoolWhitelist,
  shortTermCareWhitelist,
  socialHelpRecipientBoolWhitelist,
} from "dsl/organisms/PatientProfileDetailsShared/modelDefinition";
import isEmpty from "lodash/isEmpty";
import { useCallback } from "react";
import { getPredicamentValue } from "react-forms-state";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { GrantStatusField } from "./GrantStatusField";
import ReliefServiceGrant from "./ReliefServiceGrant";
import { ShortTermCareGrant } from "./ShortTermCareGrant";
import { PatientInfoSlugContext } from "./index";
import {
  BodyWrapper,
  BooleanField,
  Categories,
  Category,
  CategoryDescription,
  EmptiableCategory,
  EmptiableField,
  EmptiableFieldProps,
  EncryptedField,
  LocationContent,
  OntologyField,
  OntologyValue,
  PatientProfileInfoCard,
  StringField,
  StringValue,
  isWhitelisted,
} from "./shared";
import {
  ComponentMappingItem,
  Filter,
  getCategories,
  inputCollectionIsNotEmpty,
  shouldHideStreetAndHouseNumber,
} from "./utils";
import { useGuardianOptions } from "dsl/ecosystems/PatientForms/RehabForms/GKVGeneralForm/hooks";
import { toEncryptedField } from "core/model/patients/encryption/utils";
import { useRelationshipOptions } from "dsl/ecosystems/PatientForms/RehabForms/GKVGeneralForm/hooks";

type PayersWithSelectedPaymentMethod = Omit<
  PayersType,
  "selected_payment_method"
> & {
  selected_payment_method: NonNullable<PayersType["selected_payment_method"]>;
};

type GrantElement = {
  hasNotApplied: boolean;
  label: string;

  show: boolean;
  testId: string;
  value: ToType;
};

const isLivingOther = (v: PatientProfileType | null | undefined) =>
  v && v.living_situation === LIVING_SITUATION_OTHER;

function LivingSituation({
  getOntology,
  translations,
  value,
}: {
  getOntology: GetOntologyType;

  translations: Translations;
  value: { profile?: PatientProfile; search_type: SearchType };
}) {
  const isReha = value.search_type === SEARCH_TYPE_REHABILITATION;
  const hasEmptyField = isReha
    ? value?.profile?.living_situation == null ||
      value?.profile?.lift_available_state == null ||
      value?.profile?.barrier_free == null ||
      value?.profile?.unsafe_current_domestic_situation_state == 0
    : value?.profile?.living_situation == null;

  const getLivingTranslation = useCallback(
    (v: PatientProfile | null | undefined) =>
      (v?.living_situation &&
        getOntology({
          key: v.living_situation as number,
          type: ONTOLOGIES.LIVING_SITUATION,
        })) ||
      null,
    [getOntology],
  );
  const livingOther = isLivingOther(value?.profile);

  return (
    <EmptiableCategory
      hasEmptyField={hasEmptyField}
      testId="living_situation"
      title={translations.patient.patientInformation.livingSituation}
    >
      <StringField
        prefix={translations.patient.patientInformation.patientIsLiving}
        testId="profile"
        value={getLivingTranslation(value?.profile)}
      />
      {livingOther && !hasEmptyField && (
        <EmptiableField
          testId="living_situation_other"
          value={value?.profile?.living_situation_other}
        />
      )}
      <StringField
        testId="lift_available_state"
        value={getPredicamentValue({
          label: translations.patient.patientInformation.liftAvailable,
          translations,
          value: value?.profile?.lift_available_state,
        })}
      />
      <StringField
        testId="lift_available_description"
        value={value?.profile?.lift_available_description}
      />
      <StringField
        testId="barrier_free"
        value={getPredicamentValue({
          label: translations.patient.titleBarrierFree,
          translations,
          value: value?.profile?.barrier_free,
        })}
      />
      <StringField
        testId="barrier_free_description"
        value={value?.profile?.barrier_free_description}
      />
      <StringField
        testId="unsafe_current_domestic_situation_state"
        value={getPredicamentValue({
          label:
            translations.patient.patientInformation.currentDomesticSituation,
          translations,
          value: value?.profile?.unsafe_current_domestic_situation_state,
        })}
      />
      <StringField
        testId="unsafe_current_domestic_situation"
        value={value?.profile?.unsafe_current_domestic_situation}
      />
    </EmptiableCategory>
  );
}

function getDescriptionForInsurance(
  value?: CostPayerType | null | undefined,
): string | null | undefined {
  if (
    value === null ||
    typeof value === "undefined" ||
    typeof value.name !== "string"
  ) {
    return null;
  }
  return value.name;
}

export const generalInformationFilters: Record<string, Filter> = {
  carelevel: {
    exists: (auction) =>
      auction?.patient?.profile?.financing?.carelevel?.level != null,
    valueGetter: (auction) => ({
      carelevel: auction?.patient?.profile?.financing?.carelevel,
      public_care_insurance_status:
        auction?.patient?.profile?.financing?.public_care_insurance_status,
    }),
  },
  communication: {
    exists: (auction) =>
      !!auction?.patient?.profile?.communication &&
      !!(
        auction.patient.profile.communication.has_guardian ||
        auction.patient.profile.communication
          .patient_cannot_communicate_in_german ||
        auction.patient.profile.communication.patient_language ||
        auction.patient.profile.communication.patient_uses_sign_language ||
        auction.patient.profile.communication.relatives_available ||
        auction.patient.profile.communication.patient_is_contact ||
        auction.patient.profile.communication.guardian_requested
      ),
    valueGetter: (auction) => auction?.patient?.profile?.communication,
  },
  general_information: {
    exists: (auction) => auction != null,
    valueGetter: (auction) => auction,
  },
  general_practitioner: {
    exists: (auction) =>
      !!auction?.patient?.profile?.general_practitioner &&
      !!(
        auction.patient.profile.general_practitioner.name ||
        auction.patient.profile.general_practitioner.contact_details ||
        auction.patient.profile.general_practitioner.street ||
        auction.patient.profile.general_practitioner.city ||
        auction.patient.profile.general_practitioner.zipcode ||
        auction.patient.profile.general_practitioner.phone ||
        auction.patient.profile.general_practitioner.job_title ||
        auction.patient.profile.general_practitioner.honorific_prefix ||
        auction.patient.profile.general_practitioner.honorific_suffix ||
        auction.patient.profile.general_practitioner.first_name ||
        auction.patient.profile.general_practitioner.last_name
      ),
    valueGetter: (auction) => auction?.patient?.profile?.general_practitioner,
  },
  grants: {
    exists: (auction) =>
      !!auction?.patient?.profile?.financing &&
      !isEmpty(auction?.patient?.profile?.financing) &&
      auction?.search_type === SEARCH_TYPE_CARE,
    valueGetter: (auction) => auction?.patient?.profile?.financing,
  },
  health_insurance: {
    exists: (auction) =>
      !!auction?.patient?.profile?.financing
        ?.patient_has_health_insurance_state,
    valueGetter: (auction) => ({
      auction: auction,
      profile: auction?.patient?.profile,
    }),
  },
  insurance: {
    exists: (auction) => {
      const isInsuranceDefined =
        auction?.patient?.profile?.financing?.insurance?.name;
      const hasInsuranceNumber =
        auction?.patient?.profile?.financing?.insurance_number != null;

      return !!isInsuranceDefined || hasInsuranceNumber;
    },
    valueGetter: (auction) => ({
      auction: auction,
      profile: auction?.patient?.profile,
    }),
  },
  living_situation: {
    exists: (auction) =>
      !!auction?.patient &&
      inputCollectionIsNotEmpty({
        assertionObject: auction.patient.profile,
        keysToExist: [
          "living_situation",
          "lift_available_state",
          "unsafe_current_domestic_situation_state",
          "barrier_free",
        ],
      }),
    valueGetter: (auction) => ({
      profile: auction?.patient?.profile,
      search_type: auction?.search_type,
    }),
  },
  payers: {
    exists: (auction) => !!auction?.payers?.selected_payment_method,
    valueGetter: (auction) => auction?.payers,
  },
  payout: {
    exists: (auction) => {
      const payout = auction?.patient?.profile?.financing?.payout;
      return (payout && payout !== 0) === true;
    },
    valueGetter: (auction) => auction?.patient?.profile?.financing?.payout,
  },
  preferences: {
    exists: (auction) => {
      if (!auction?.patient?.profile?.preferences) {
        return false;
      }
      const {
        accompanying_person,
        barrier_free_room,
        room_type,
        specialist_doctor,
      } = auction?.patient?.profile.preferences ?? {};
      return (
        ((room_type != 0 && room_type != null) ||
          accompanying_person ||
          barrier_free_room ||
          specialist_doctor) === true
      );
    },
    valueGetter: (auction) => auction?.patient?.profile?.preferences,
  },
  relavant_information: {
    exists: (auction) => auction != null,
    valueGetter: (auction) => auction?.patient,
  },
  station: {
    exists: (auction) => !!auction?.patient.station_full,
    valueGetter: (auction: Auction | null | undefined) => ({
      station_full: auction?.patient?.station_full,
    }),
  },
};

function getWhitelist({ formInputValue }: { formInputValue: Auction }) {
  return {
    carelevel: matchesWhitelistScopes(carelevelWhitelist)({
      formInputValue,
    }),
    communication: true,

    general_information: true,
    general_practitioner: true,
    grants: formInputValue?.assessment_variant !== ASSESSMENT_SLUG.MOBILE_SHORT,
    insurance: matchesWhitelistScopes(insuranceWhitelist)({
      formInputValue,
    }),
    living_situation: true,
    payers: true,
    payout: true,
    preferences: true,
    station:
      formInputValue.search_type === SEARCH_TYPE_MEDICAL_SUPPLIES ||
      formInputValue.search_type === SEARCH_TYPE_HOME_CARE,
  };
}

const generalInfoComponentMapping: ComponentMappingItem[] = [
  {
    Component: InformationCardGeneralInformation,
    key: "general_information",
  },
  {
    Component: Insurance,
    key: "insurance",
  },
  {
    Component: HealthInsurance,
    key: "health_insurance",
  },
  {
    Component: Carelevel,
    key: "carelevel",
  },
  {
    Component: Payout,
    key: "payout",
  },
  {
    Component: LivingSituation,
    key: "living_situation",
  },
  {
    Component: GeneralPractitioner,
    key: "general_practitioner",
  },
  {
    Component: Communication,
    key: "communication",
  },
  {
    Component: Grants,
    key: "grants",
  },
  {
    Component: Payers,
    key: "payers",
  },
  {
    Component: Preferences,
    key: "preferences",
  },
  {
    Component: StationFull,
    key: "station",
  },
];

function InformationCardGeneralInformation({
  getOntology,
  isSealdOnly,
  translations,
  value,
  withEncryptionAccess,
}: {
  getOntology: GetOntologyType;
  isSealdOnly: boolean;
  translations: Translations;
  value: Auction | null | undefined;
  withEncryptionAccess: boolean;
}) {
  const birthDate = value?.patient?.profile?.birth_date?.decrypted;
  const isProviderSearchPatient =
    value?.patient?.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH;

  const hideStreetAndHouseNumber = shouldHideStreetAndHouseNumber(
    value?.search_type,
    value?.solutions,
  );

  const birthDateValue = birthDate
    ? getAgeFromDate(birthDate).toString()
    : (
        getAge(value?.patient?.profile?.age_interval, value?.search_type) || ""
      ).toString();

  const heightIntervalValue = getInterval(
    value?.patient,
    value?.search_type,
    isProviderSearchPatient,
    "height_interval",
    translations,
  );

  const weightIntervalValue = getInterval(
    value?.patient,
    value?.search_type,
    isProviderSearchPatient,
    "weight_interval",
    translations,
  );

  return (
    <Category
      testId="general_information"
      title={translations.patient.patientInformation.generalInformation}
    >
      <EncryptedField
        bold
        isSealdOnly={isSealdOnly}
        prefix={translations.patient.firstName}
        testId="first_name"
        value={value?.patient?.profile?.first_name}
        withEncryptionAccess={withEncryptionAccess}
      />
      <EncryptedField
        bold
        isSealdOnly={isSealdOnly}
        prefix={translations.patient.lastName}
        testId="last_name"
        value={value?.patient?.profile?.last_name}
        withEncryptionAccess={withEncryptionAccess}
      />
      {value?.patient?.profile?.gender && (
        <BodyWrapper>
          <b className="-mr-3 font-bold">{`${translations.patient.gender}${translations.general.colon} `}</b>
          <OntologyValue
            getOntology={getOntology}
            type="gender"
            value={value.patient.profile.gender}
          />
        </BodyWrapper>
      )}
      <EncryptedField
        bold
        isSealdOnly={isSealdOnly}
        prefix={translations.patient.dateOfBirth}
        testId="birth_date"
        value={value?.patient?.profile?.birth_date}
        withDateString
        withEncryptionAccess={withEncryptionAccess}
      />
      {birthDateValue && (
        <BodyWrapper>
          <b className="-mr-3 font-bold">{`${translations.patient.age}${translations.general.colon} `}</b>
          <StringValue testId="age_interval" value={birthDateValue} />
        </BodyWrapper>
      )}
      {withEncryptionAccess ? (
        <EncryptedField
          bold
          hideEmpty
          isSealdOnly={isSealdOnly}
          prefix={translations.patient.formGeneration.height}
          suffix="cm"
          testId="height"
          value={value?.patient?.profile?.height}
          withEncryptionAccess
        />
      ) : heightIntervalValue ? (
        <BodyWrapper>
          <HorizontalLayout>
            <b className="-mr-3 font-bold">{`${translations.patient.formGeneration.height}${translations.general.colon} `}</b>
            <Body>
              <StringValue
                testId="height_interval"
                value={`${heightIntervalValue}cm`}
              />
            </Body>
          </HorizontalLayout>
        </BodyWrapper>
      ) : null}
      {withEncryptionAccess ? (
        <EncryptedField
          bold
          hideEmpty
          isSealdOnly={isSealdOnly}
          prefix={translations.patient.formGeneration.weight}
          suffix="kg"
          testId="weight"
          value={value?.patient?.profile?.weight}
          withEncryptionAccess
        />
      ) : weightIntervalValue ? (
        <BodyWrapper>
          <HorizontalLayout>
            <b className="-mr-3 font-bold">{`${translations.patient.formGeneration.weight}${translations.general.colon} `}</b>
            <Body>
              <StringValue
                testId="weight_interval"
                value={`${weightIntervalValue}kg`}
              />
            </Body>
          </HorizontalLayout>
        </BodyWrapper>
      ) : null}
      <LocationContent
        hideStreetAndHouseNumber={hideStreetAndHouseNumber}
        location={value?.profile?.search_location}
        testId="search_location"
      />
    </Category>
  );
}

function HealthInsurance({
  translations,
  value,
}: {
  translations: Translations;
  value: PatientType | null | undefined;
}) {
  const financing = value?.profile?.financing;

  return (
    <Category
      testId="health_insurance"
      title={translations.patient.patientInformation.insurance}
    >
      <EmptiableField
        noEmptyValue
        testId="patient_has_health_insurance_state"
        title={translations.patient.hasHealthInsurance}
        value={getPredicamentValue({
          translations,
          value: financing?.patient_has_health_insurance_state,
        })}
      />
    </Category>
  );
}

function Insurance({
  isSealdOnly,
  translations,
  value,
  withEncryptionAccess,
}: {
  isSealdOnly: boolean;
  translations: Translations;
  value: PatientType | null | undefined;
  withEncryptionAccess: boolean;
}) {
  const financing = value?.profile?.financing;

  return (
    <Category
      testId="insurance"
      title={translations.patient.patientInformation.insurance}
    >
      <StringField
        prefix={translations.patient.insuranceProvider}
        testId="financing_insurance_name"
        value={financing?.insurance?.name}
      />
      <EncryptedField
        hideEmpty
        isSealdOnly={isSealdOnly}
        prefix={translations.patient.insuranceNumber}
        testId="financing_insurance_number"
        value={financing?.insurance_number}
        withEncryptionAccess={withEncryptionAccess}
      />
    </Category>
  );
}
function StationFull({
  translations,
  value,
}: {
  translations: Translations;
  value: PatientType | null | undefined;
}) {
  const station = value?.station_full;
  const phoneNumber = value?.station_full?.phone_number;

  return (
    <Category testId="station_full" title={translations.patient.station}>
      <StringField
        prefix={translations.patient.station}
        testId="station_name"
        value={
          station?.name
            ? concatStrings(station.name, station.description)
            : null
        }
      />
      <StringField
        prefix={translations.patient.stationPhoneNumber}
        testId="station_phone_number"
        value={phoneNumber ?? null}
      />
    </Category>
  );
}

function Carelevel({
  translations,
  value,
}: {
  translations: Translations;
  value: FinancingType | null | undefined;
}) {
  return (
    <Category testId="care_level" title={translations.patient.carelevel}>
      <StringField
        testId="carelevel"
        value={getCareLevelValue(value?.carelevel, translations)?.label}
      />
      <BooleanField
        label={translations.patient.hasAppliedforHigherCarelevel}
        value={!!value?.carelevel?.higher_level}
      />
      <BooleanField
        label={translations.patient.expeditedRequest}
        value={!!value?.carelevel?.expedited_request}
      />

      <BooleanField
        label={translations.patient.hasAppliedforCarelevel}
        value={!!value?.carelevel?.has_applied}
      />
      <BooleanField
        label={translations.patient.publicCareInsuranceStatus}
        value={value?.public_care_insurance_status}
      />
    </Category>
  );
}

function Payout({
  getOntology,
  translations,
  value,
}: {
  getOntology: GetOntologyType;
  translations: Translations;
  value: number | null | undefined;
}) {
  return (
    <Category
      testId="payout"
      title={translations.patient.patientInformation.payout}
    >
      <CategoryDescription
        value={
          getOntology({ key: value, type: ONTOLOGIES.GRANTS_TYPE }) || null
        }
      />
    </Category>
  );
}

function GeneralPractitioner({
  isSealdOnly,
  translations,
  value,
  withEncryptionAccess,
}: {
  isSealdOnly: boolean;
  withEncryptionAccess: boolean;
  translations: Translations;
  value: GeneralPractitionerType | null | undefined;
}) {
  if (!value) return null;

  const hasLegacyFields = !!(value.name || value.contact_details);

  const fullName = buildLine(
    value.honorific_prefix?.decrypted,
    value.first_name?.decrypted,
    value.last_name?.decrypted,
    value.honorific_suffix?.decrypted,
  );

  const zipCodeCity = buildLine(
    value.zipcode?.decrypted,
    value.city?.decrypted,
  );

  const streetAddress = buildLine(
    value.street?.decrypted,
    value.house_number?.decrypted,
  );

  const fullAddress = buildLine(
    zipCodeCity ? `${zipCodeCity},` : "",
    streetAddress,
  );

  return (
    <Category
      testId="general_practitioner"
      title={translations.patient.patientInformation.generalPractitionerTitle}
    >
      {hasLegacyFields ? (
        <>
          <StringField testId="general_practitioner_name" value={value.name} />
          <StringField
            testId="general_practitioner_contact_details"
            value={value.contact_details}
          />
        </>
      ) : (
        <>
          <EncryptedField
            bold
            isSealdOnly={isSealdOnly}
            prefix={
              translations.patient.patientInformation
                .generalPractitionerNamePlaceholder
            }
            testId="general_practitioner_full_name"
            value={toEncryptedField(fullName)}
            withEncryptionAccess={withEncryptionAccess}
          />
          <EncryptedField
            bold
            isSealdOnly={isSealdOnly}
            prefix={
              translations.patient.patientInformation
                .generalPractitionerJobTitle
            }
            testId="general_practitioner_job_title"
            value={value.job_title}
            withEncryptionAccess={withEncryptionAccess}
          />
          <EncryptedField
            bold
            isSealdOnly={isSealdOnly}
            prefix={translations.address.address}
            testId="general_practitioner_address"
            value={toEncryptedField(fullAddress)}
            withEncryptionAccess={withEncryptionAccess}
          />
          <EncryptedField
            bold
            isSealdOnly={isSealdOnly}
            prefix={translations.contactInformation.phoneNumber}
            testId="general_practitioner_phone"
            value={value.phone}
            withEncryptionAccess={withEncryptionAccess}
          />
        </>
      )}
    </Category>
  );
}

function Grants({
  auction,
  getOntology,
  translations,
  value,
}: {
  auction: Auction;
  getOntology: GetOntologyType;
  translations: Translations;
  value: FinancingType | null | undefined;
}) {
  if (!isWhitelisted(grantsWhitelist, auction)) {
    return null;
  }

  const grantElements: GrantElement[] = [
    {
      hasNotApplied: true,
      label: translations.patient.reimbursmentForHelpAtHome,
      show: isWhitelisted(reimbursment_for_help_at_homeWhitelist, auction),
      testId: "reimbursment_for_help_at_home",
      value: value?.reimbursment_for_help_at_home,
    },
    {
      hasNotApplied: true,
      label: translations.patient.reimbursmentSecured,
      show: isWhitelisted(reimbursment_securedWhitelist, auction),
      testId: "reimbursment_secured",
      value: value?.reimbursment_secured,
    },
    {
      hasNotApplied: true,
      label: translations.patient.preventativeCare,
      show: true,
      testId: "preventative_care",
      value: value?.preventative_care,
    },
    {
      hasNotApplied: false,
      label: translations.patient.socialHelpRecipient,
      show: isWhitelisted(socialHelpRecipientBoolWhitelist, auction),
      testId: "social_help_recipient_bool",
      value: value?.social_help_recipient_bool,
    },
    {
      hasNotApplied: false,
      label: translations.patient.patientInformation.acceptsPrivatePayment,
      show: true,
      testId: "private_payment_bool",
      value: value?.private_payment_bool,
    },
  ];

  const shortTermGrant: GrantElement = {
    hasNotApplied: false,
    label: translations.patient.shortTermCareGrant,
    show: isWhitelisted(shortTermCareWhitelist, auction),
    testId: "short_term_care",
    value: value?.short_term_care,
  };

  const reliefServices: GrantElement = {
    hasNotApplied: false,
    label: translations.patient.reliefServices,
    show: isWhitelisted(reliefServiceBoolWhitelist, auction),
    testId: "relief_services",
    value: value?.relief_services,
  };

  const checkedGrants: GrantElement[] = [
    ...grantElements,
    shortTermGrant,
    reliefServices,
  ].filter(
    (element: GrantElement) =>
      element.value &&
      typeof element.label === "string" &&
      element.label.length > 0 &&
      element.show,
  );

  return (
    <Category testId="grants" title={translations.patient.grants}>
      {grantElements.map((element: GrantElement) =>
        element.show ? (
          <GrantStatusField
            key={`grant-key-${element.label}`}
            hasNotApplied={element.hasNotApplied}
            label={element.label}
            testId={element.testId}
            value={element.value}
          />
        ) : null,
      )}

      {shortTermGrant.show && (
        <ShortTermCareGrant
          getOntology={getOntology}
          testId={shortTermGrant.testId}
          value={shortTermGrant.value}
        />
      )}
      {reliefServices.show && (
        <ReliefServiceGrant
          testId={reliefServices.testId}
          value={reliefServices.value}
        />
      )}
      <EmptiableField
        noEmptyValue
        testId="more_information"
        title={translations.patient.patientInformation.grantsMoreInformation}
        value={value?.more_information}
      />
      {!checkedGrants.length && (
        <StringValue
          style={{ color: TEXT_DARK_SECONDARY }}
          testId="grant_status"
          value={translations.patient.patientInformation.financingNotSelected}
        />
      )}
    </Category>
  );
}

function Payers({
  translations,
  value,
}: {
  translations: Translations;
  value: PayersWithSelectedPaymentMethod;
}) {
  const labels = getPayersLabels(translations);

  const selectedField = PAYERS_MAPPING[value?.selected_payment_method];
  const config: Record<
    keyof PayersType,
    Pick<EmptiableFieldProps, "title" | "value" | "testId">
  > = {
    civil_servants_aid: {
      testId: "civil_servants_aid_description",
      title: labels.civil_servants_aid_description,
      value: value?.civil_servants_aid,
    },
    free_from_supplementary_payment_state: {
      testId: "free_from_supplementary_payment_state",
      title: labels.free_from_supplementary_payment_state,
      value: getPredicamentValue({
        translations,
        value: value?.free_from_supplementary_payment_state,
      }),
    },
    further_information: {
      testId: "further_information",
      title: labels.further_information,
      value: value?.further_information,
    },
    insurance: {
      testId: "payers_insurance",
      title: labels.insurance,
      value: getDescriptionForInsurance(value?.insurance),
    },
    other_payer: {
      testId: "other_payer_description",
      title: labels.other_payer_description,
      value: value?.other_payer,
    },
    public_accident_insurance: {
      testId: "public_accident_insurance_description",
      title: labels.public_accident_insurance_description,
      value: value?.public_accident_insurance,
    },
    public_pension: {
      testId: "public_accident_insurance_description",
      title: labels.public_accident_insurance_description,
      value: getDescriptionForInsurance(value?.public_pension),
    },
    selected_payment_method: {
      testId: "selected_payment_method",
      title: labels.selected_payment_method,
      value: value?.selected_payment_method
        ? labels[selectedField as keyof typeof labels]
        : undefined,
    },
    self_payer: {
      testId: "self_payer_description",
      title: labels.self_payer_description,
      value: value?.self_payer,
    },
    social_service_department: {
      testId: "social_service_department_description",
      title: labels.social_service_department_description,
      value: value?.social_service_department,
    },
    supplementary_insurance: {
      testId: "supplementary_insurance",
      value: value?.supplementary_insurance,
    },
    supplementary_insurance_state: {
      testId: "supplementary_insurance_state",
      value: getPredicamentValue({
        label: labels.supplementary_insurance,
        translations,
        value: value?.supplementary_insurance_state,
      }),
    },
    supplementary_payment: {
      testId: "supplementary_payment",
      value: value?.supplementary_payment,
    },
    supplementary_payment_state: {
      testId: "supplementary_payment_state",
      value: getPredicamentValue({
        label: labels.supplementary_payment,
        translations,
        value: value?.supplementary_payment_state,
      }),
    },
  };

  return (
    <EmptiableCategory
      hasEmptyField={!value[selectedField]}
      testId="payers"
      title={translations.patient.patientInformation.payers}
    >
      {Object.entries(config).map(([field, { testId, title, value }]) => {
        return (
          <EmptiableField
            key={field}
            noEmptyValue
            testId={testId}
            title={title}
            value={value}
          />
        );
      })}
    </EmptiableCategory>
  );
}

function Communication({
  isSealdOnly,
  translations,
  value,
  withEncryptionAccess,
}: {
  isSealdOnly: boolean;

  translations: Translations;
  value: PatientCommunicationType | null | undefined;

  withEncryptionAccess: boolean;
}) {
  const guardianOptions = useGuardianOptions();
  const relationshipOptions = useRelationshipOptions();

  return (
    <Category
      testId="communication"
      title={translations.patient.patientInformation.decisionsLanguages}
    >
      {value?.patient_is_contact_description ? (
        <EncryptedField
          bold
          isSealdOnly={isSealdOnly}
          prefix={translations.patient.patientInformation.contactIsPatient}
          testId="patient_is_contact_description"
          value={value.patient_is_contact_description}
          verticalLayout
          withEncryptionAccess={withEncryptionAccess}
        />
      ) : (
        <BooleanField
          label={translations.patient.patientInformation.contactIsPatient}
          value={value?.patient_is_contact}
        />
      )}
      {value?.relatives_description ? (
        <EncryptedField
          bold
          isSealdOnly={isSealdOnly}
          prefix={translations.patient.patientInformation.relativesAvailable}
          testId="relatives_description"
          value={value.relatives_description}
          verticalLayout
          withEncryptionAccess={withEncryptionAccess}
        />
      ) : (
        value?.relatives_available && (
          <>
            <BooleanField
              label={translations.patient.patientInformation.relativesAvailable}
              value={value?.relatives_available}
            />
            {value?.relatives?.map((relative, index) => {
              const relativeNumber = index + 1;
              const zipCodeCity = buildLine(
                relative.zipcode?.decrypted,
                relative.city?.decrypted,
              );

              const streetAddress = buildLine(
                relative.street?.decrypted,
                relative.house_number?.decrypted,
              );

              const fullAddress = buildLine(
                zipCodeCity ? `${zipCodeCity},` : "",
                streetAddress,
              );

              return (
                <div key={relativeNumber} className="pb-4">
                  <h4 className="ml-4 font-medium">
                    {`${translations.patient.patientInformation.relative} ${relativeNumber}${translations.general.colon}`}
                  </h4>
                  <EncryptedField
                    bold
                    hideEmpty
                    isSealdOnly={isSealdOnly}
                    prefix={translations.patient.firstName}
                    testId={`relative_first_name_${relativeNumber}`}
                    value={relative.first_name}
                    withEncryptionAccess={withEncryptionAccess}
                  />
                  <EncryptedField
                    bold
                    hideEmpty
                    isSealdOnly={isSealdOnly}
                    prefix={translations.patient.lastName}
                    testId={`relative_last_name_${relativeNumber}`}
                    value={relative.last_name}
                    withEncryptionAccess={withEncryptionAccess}
                  />
                  <EncryptedField
                    bold
                    hideEmpty
                    isSealdOnly={isSealdOnly}
                    prefix={translations.contactInformation.phoneNumber}
                    testId={`relative_phone_${relativeNumber}`}
                    value={relative.phone}
                    withEncryptionAccess={withEncryptionAccess}
                  />
                  {relative?.relationship?.decrypted &&
                    (parseInt(relative?.relationship?.decrypted) ==
                    RELATIONSHIP_TO_PATIENT.OTHER ? (
                      <EncryptedField
                        bold
                        hideEmpty
                        isSealdOnly={isSealdOnly}
                        prefix={translations.ontologies.relationship.key}
                        testId={`relative_relationship_other_${relativeNumber}`}
                        value={relative?.relationship_other}
                        withEncryptionAccess={withEncryptionAccess}
                      />
                    ) : (
                      <EncryptedField
                        bold
                        hideEmpty
                        isSealdOnly={isSealdOnly}
                        prefix={translations.ontologies.relationship.key}
                        testId={`relative_relationship_${relativeNumber}`}
                        value={toEncryptedField(
                          relationshipOptions.find(
                            (option) =>
                              option.value ==
                              parseInt(relative?.relationship?.decrypted ?? ""),
                          )?.label ?? "",
                        )}
                        withEncryptionAccess={withEncryptionAccess}
                      />
                    ))}
                  {fullAddress && (
                    <EncryptedField
                      bold
                      isSealdOnly={isSealdOnly}
                      prefix={translations.address.address}
                      testId={`relative_address_${relativeNumber}`}
                      value={toEncryptedField(fullAddress)}
                      withEncryptionAccess={withEncryptionAccess}
                    />
                  )}
                </div>
              );
            })}
          </>
        )
      )}
      {value?.guardian_contact_information ? (
        <EncryptedField
          bold
          isSealdOnly={isSealdOnly}
          prefix={translations.patient.patientInformation.hasGuardian}
          testId="guardian_contact_information"
          value={value?.guardian_contact_information}
          verticalLayout
          withEncryptionAccess={withEncryptionAccess}
        />
      ) : (
        value?.guardians?.map((guardian, index) => {
          const guardianNumber = index + 1;
          const zipCodeCity = buildLine(
            guardian.zipcode?.decrypted,
            guardian.city?.decrypted,
          );

          const streetAddress = buildLine(
            guardian.street?.decrypted,
            guardian.house_number?.decrypted,
          );

          const fullAddress = [zipCodeCity, streetAddress]
            .filter(Boolean)
            .join(", ");

          return (
            <div key={guardianNumber} className="pb-4">
              <h4 className="ml-4 font-bold">
                {`${translations.patient.formGeneration.guardianIsCustodian} ${guardianNumber}${translations.general.colon}`}
              </h4>
              <EncryptedField
                bold
                hideEmpty
                isSealdOnly={isSealdOnly}
                prefix={translations.patient.firstName}
                testId={`guardian_first_name_${guardianNumber}`}
                value={guardian.first_name}
                withEncryptionAccess={withEncryptionAccess}
              />
              <EncryptedField
                bold
                hideEmpty
                isSealdOnly={isSealdOnly}
                prefix={translations.patient.lastName}
                testId={`guardian_last_name_${guardianNumber}`}
                value={guardian.last_name}
                withEncryptionAccess={withEncryptionAccess}
              />
              <EncryptedField
                bold
                hideEmpty
                isSealdOnly={isSealdOnly}
                prefix={translations.contactInformation.phoneNumber}
                testId={`guardian_phone_${guardianNumber}`}
                value={guardian.phone}
                withEncryptionAccess={withEncryptionAccess}
              />
              {guardian?.type && (
                <BodyWrapper>
                  <b className="-mr-3 font-bold">{`${translations.report.solution}${translations.general.colon} `}</b>
                  <StringField
                    testId={`guardian_type_${guardianNumber}`}
                    value={
                      guardianOptions.find(
                        (option) => option.value === guardian?.type,
                      )?.label
                    }
                  />
                </BodyWrapper>
              )}
              {fullAddress && (
                <EncryptedField
                  bold
                  isSealdOnly={isSealdOnly}
                  prefix={translations.address.address}
                  testId={`guardian_address_${guardianNumber}`}
                  value={toEncryptedField(fullAddress)}
                  withEncryptionAccess={withEncryptionAccess}
                />
              )}
            </div>
          );
        })
      )}
      <BooleanField
        label={translations.patient.patientInformation.guardianRequested}
        value={value?.guardian_requested}
      />
      <EmptiableField
        bold
        testId="patient_cannot_communicate_in_german"
        title={
          translations.patient.patientInformation
            .patientCannotCommunicateInGerman.label
        }
        value={
          value?.patient_cannot_communicate_in_german
            ? value.patient_language
            : null
        }
      />
      <EmptiableField
        bold
        testId="patient_uses_sign_language"
        title={
          translations.patient.patientInformation.patientUsesSignInLanguage
            .label
        }
        value={
          value?.patient_uses_sign_language
            ? value.patient_uses_sign_language
            : null
        }
      />
    </Category>
  );
}

function Preferences({
  getOntology,
  translations,
  value,
}: {
  getOntology: GetOntologyType;

  translations: Translations;
  value: CarePreferencesType | null | undefined;
}) {
  return (
    <Category
      testId="preferences"
      title={translations.patient.patientInformation.specialRequirements}
    >
      <OntologyField
        getOntology={getOntology}
        type="roomType"
        value={value?.room_type}
      />
      <BooleanField
        label={translations.patient.barrierFreeRoom}
        value={value?.barrier_free_room}
      />
      <BooleanField
        label={translations.patient.accompanyingPerson}
        value={value?.accompanying_person}
      />
      <BooleanField
        label={translations.patient.specialistDoctor}
        value={value?.specialist_doctor}
      />
    </Category>
  );
}

export function InformationCard({ auction }: { auction: Auction }) {
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const { isReceiverApp } = useEnvContext();
  const title = isReceiverApp
    ? translations.patient.patientInformation.stepperTitle
    : undefined;
  const categories = getCategories({
    auction,
    componentMapping: generalInfoComponentMapping,
    filters: generalInformationFilters,
    forClinic: !isReceiverApp,
    getWhitelist,
  });

  return (
    <PatientInfoSlugContext.Provider
      value={{
        assessmentSlug: ASSESSMENT_SLUG.INFO,
        auctionId: auction.id,
        patientId: auction.patient.id,
      }}
    >
      <PatientProfileInfoCard title={title}>
        <Categories
          auction={auction}
          categories={categories}
          getOntology={getOntology}
          translations={translations}
        />
      </PatientProfileInfoCard>
    </PatientInfoSlugContext.Provider>
  );
}
